import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CircleNotch, MagnifyingGlass } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { money } from "@/components/opportunities/shared";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

function linkedSummary(r) {
  return Object.entries(r.linked)
    .filter(([, n]) => n > 0)
    .map(([k, n]) => `${n} ${k.replace(/_/g, " ")}`)
    .join(", ") || "no linked records";
}

function RecordRow({ r, checked, onToggle }) {
  return (
    <label className="flex cursor-pointer items-start gap-2.5 rounded-sm px-2 py-1.5 text-sm hover:bg-[#F4F4F5]"
      data-testid={`record-purge-option-${r.id}`}>
      <Checkbox checked={checked} onCheckedChange={onToggle} className="mt-0.5" />
      <span className="min-w-0 flex-1">
        <span className="block truncate font-medium text-[#09090B]">
          {r.organization_name}
          <span className="ml-1.5 text-xs font-normal capitalize text-[#6D6D70]">· {r.lifecycle_status || "prospect"}</span>
        </span>
        <span className="block text-[11px] text-[#6D6D70]">{linkedSummary(r)}</span>
        {r.financial_history && (
          <span className="block text-[11px] font-semibold text-[#B45309]" data-testid={`record-financial-${r.id}`}>
            Carries financial history{r.collections_total > 0 ? ` — ${money(r.collections_total)} collected` : ""} — requires explicit acknowledgment
          </span>
        )}
      </span>
    </label>
  );
}

// Global-Admin permanent purge of TEST prospect/client records and everything
// linked to them (contacts, properties, activities, opportunities, collections,
// commissions). Requires a written reason and typing PURGE; records that carry
// financial history additionally require an explicit acknowledgment. Every
// purge leaves an audit-trail entry that survives the deletion.
export function RecordsPurgeDialog({ open, onClose, onDone }) {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState([]);
  const [reason, setReason] = useState("");
  const [ackFinancial, setAckFinancial] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelected([]); setReason(""); setAckFinancial(false); setConfirmText(""); setRows(null); setQ("");
    api.get("/records/purge-preview")
      .then(({ data }) => setRows(data))
      .catch(() => toast.error("Purge preview could not be loaded."));
  }, [open]);

  const search = async () => {
    setRows(null);
    try {
      const { data } = await api.get("/records/purge-preview", { params: { q } });
      setRows(data);
    } catch {
      toast.error("Purge preview could not be loaded.");
    }
  };

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const selectedRows = (rows || []).filter((r) => selected.includes(r.id));
  const hasFinancial = selectedRows.some((r) => r.financial_history);
  const confirmed = confirmText.trim().toUpperCase() === "PURGE";
  const ready = selected.length > 0 && reason.trim() && confirmed && (!hasFinancial || ackFinancial);

  const submit = async () => {
    if (!ready) return;
    setLoading(true);
    try {
      const { data } = await api.post("/records/purge", {
        prospect_ids: selected, reason: reason.trim(), acknowledge_financial: ackFinancial,
      });
      const skippedMsg = data.skipped.length ? ` · ${data.skipped.length} skipped` : "";
      toast.success(`${data.purged.length} record(s) permanently purged${skippedMsg}`);
      onDone();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto rounded-sm sm:max-w-xl" data-testid="records-purge-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold text-[#032852]">Purge Test Records</DialogTitle>
          <DialogDescription>
            Permanently removes a prospect or client and everything linked to it — contacts, properties,
            activities, opportunities, collections and commissions. This cannot be undone.
            An audit-trail entry is kept for every purge.
          </DialogDescription>
        </DialogHeader>
        <div className="flex gap-2">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search organizations…"
            onKeyDown={(e) => e.key === "Enter" && search()}
            className="rounded-sm" data-testid="records-purge-search" />
          <Button variant="outline" className="rounded-sm" onClick={search} data-testid="records-purge-search-btn">
            <MagnifyingGlass size={16} />
          </Button>
        </div>
        {rows === null ? (
          <div className="flex justify-center py-10"><CircleNotch size={22} className="animate-spin text-[#032852]" /></div>
        ) : (
          <>
            <div className="max-h-64 space-y-1 overflow-y-auto rounded-sm border border-[#E4E4E7] p-2">
              {rows.map((r) => (
                <RecordRow key={r.id} r={r} checked={selected.includes(r.id)} onToggle={() => toggle(r.id)} />
              ))}
              {rows.length === 0 && <p className="py-6 text-center text-sm text-[#6D6D70]">No matching records.</p>}
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wide text-[#52525B]">Reason (recorded in the audit trail)</Label>
              <Input value={reason} onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Test data created during setup" className="rounded-sm" data-testid="records-purge-reason" />
            </div>
            {hasFinancial && (
              <label className="flex items-start gap-2.5 rounded-sm border border-[#B45309]/40 bg-[#F59E0B]/10 p-3 text-xs text-[#7C4A03]"
                data-testid="records-purge-ack">
                <Checkbox checked={ackFinancial} onCheckedChange={(v) => setAckFinancial(v === true)} className="mt-0.5" />
                <span>
                  I understand the selected record(s) carry <b>financial history</b> (won deals, collections or
                  commissions) and that purging permanently removes those ledger entries too.
                </span>
              </label>
            )}
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wide text-[#52525B]">
                Type <span className="text-[#B91C1C]">PURGE</span> to confirm permanent removal of {selected.length} record(s)
              </Label>
              <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)}
                placeholder="PURGE" className="rounded-sm" data-testid="records-purge-confirm-input" />
            </div>
          </>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" className="rounded-sm" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={loading || !ready}
            className="rounded-sm bg-[#EF4444] font-semibold text-white hover:bg-[#DC2626]" data-testid="submit-records-purge">
            {loading ? <CircleNotch size={18} className="animate-spin" /> : `Permanently purge ${selected.length || ""} record(s)`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
